import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, PhoneCall, Truck, ChevronDown, ChevronRight, Music2 } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsShopOpen(false);
  }, [location]);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Support', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const linkClass = (active) =>
    `px-3.5 py-2 text-sm font-medium tracking-wide transition-colors ${active ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`;

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Utility bar */}
      <div className="bg-dark-900 text-dark-200 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="flex items-center gap-2 tracking-wide">
            <Truck className="w-3.5 h-3.5 text-elite-400" />
            <span className="font-medium text-white">Free USPS shipping</span>
            <span className="hidden sm:inline text-dark-300">on every order in the USA</span>
          </p>
          <div className="flex items-center gap-4 tracking-wide">
            <a href={`mailto:${CONTACT_INFO.email}`} className="hidden lg:inline text-dark-300 hover:text-white transition-colors">{CONTACT_INFO.email}</a>
            <a href={phoneHref} className="flex items-center gap-1.5 font-medium text-white hover:text-elite-400 transition-colors">
              <PhoneCall className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-dark-50/95 backdrop-blur border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[76px] gap-4">
            <Logo />

            {/* Desktop primary nav */}
            <div className="hidden lg:flex items-center gap-1">
              <Link to="/" className={linkClass(location.pathname === '/')}>Home</Link>

              {/* Instruments dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsShopOpen(true)}
                onMouseLeave={() => setIsShopOpen(false)}
              >
                <Link
                  to="/shop"
                  className={`flex items-center gap-1 ${linkClass(location.pathname === '/shop')}`}
                >
                  Instruments <ChevronDown className={`w-4 h-4 transition-transform ${isShopOpen ? 'rotate-180' : ''}`} />
                </Link>
                {isShopOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[560px] animate-fade-in">
                    <div className="bg-white border border-dark-200 shadow-elevated rounded-2xl overflow-hidden">
                      <div className="grid grid-cols-2 gap-1 p-3">
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/shop?category=${cat.slug}`}
                            className="group flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-dark-50 transition-colors"
                          >
                            <span className="mt-0.5 w-9 h-9 rounded-full bg-athletic-50 text-athletic-600 flex items-center justify-center flex-shrink-0">
                              <Music2 className="w-4 h-4" />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-dark-900 group-hover:text-athletic-700">{cat.name}</span>
                              <span className="block text-xs text-dark-500 line-clamp-1">{cat.description}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                      <Link to="/shop" className="flex items-center justify-between px-5 py-3.5 bg-dark-900 text-white text-sm font-medium hover:bg-athletic-600 transition-colors">
                        Browse the full collection
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {navLinks.slice(1).map((link) => (
                <Link key={link.path} to={link.path} className={linkClass(location.pathname === link.path)}>
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/shop" className="hidden xl:inline-flex items-center gap-2 px-5 py-2.5 bg-athletic-500 text-white text-sm font-semibold tracking-wide rounded-full hover:bg-athletic-600 transition-colors shadow-button">
                Shop Now
              </Link>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-full flex items-center justify-center text-dark-900 border border-dark-300 hover:border-athletic-500 hover:text-athletic-600 transition-colors"
                aria-label="Cart"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-athletic-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-dark-50">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-full flex items-center justify-center text-dark-900 border border-dark-300"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-dark-200 bg-dark-50">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center justify-between px-4 py-3 text-base font-medium text-dark-800 hover:bg-white hover:text-athletic-600 rounded-xl"
                >
                  {link.name}
                  <ChevronRight className="w-4 h-4 text-dark-300" />
                </Link>
              ))}
              <div className="pt-3 border-t border-dark-200 mt-3">
                <p className="px-4 text-xs uppercase tracking-[0.2em] text-dark-400 mb-2 flex items-center gap-2"><Music2 className="w-3.5 h-3.5 text-athletic-500" /> Instruments</p>
                <Link to="/shop" className="block px-4 py-2.5 text-sm font-semibold text-athletic-700 hover:bg-white rounded-xl">Shop All</Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-white hover:text-athletic-600 rounded-xl"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-full bg-dark-900 text-white font-medium">
                <PhoneCall className="w-4 h-4 text-elite-400" /> Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
